// =============================================================================
// domain/coachProse.ts — model prose, turned into data the screen can render.
// -----------------------------------------------------------------------------
// ── 🚨 NO MARKUP PATH, EVER ──
//
// The old client escapes the model's text and then assigns `innerHTML`, which is
// safe exactly as long as the escape is right — on a page holding the trader's
// session, for a string that came from a language model, on a payload the model
// was handed by an import file. Three degrees from something a stranger wrote.
//
// This splits the prose into blocks and inline spans, and the component renders
// elements. There is no string that ever becomes HTML, so there is no escape to
// get wrong. Same rule `verdictSpans` follows for the structured verdict; this
// is the freeform version of it.
//
// ⚠ IT HANDLES WHAT THE COACH ACTUALLY WRITES, and no more: headings, bullets,
// paragraphs, and `**bold**`. Anything it does not recognise — a table, a code
// fence, a link — comes through as its own literal text rather than vanishing.
// Dropping an unrecognised line would silently lose part of an answer the
// trader paid for.
// =============================================================================

export interface ProseSpan { text: string; bold: boolean }

export type ProseBlock =
  | { kind: 'heading'; level: 1 | 2 | 3; spans: ProseSpan[] }
  | { kind: 'para'; spans: ProseSpan[] }
  | { kind: 'list'; items: ProseSpan[][] };

/** `a **b** c` → three spans. Unbalanced `**` stays literal. */
export function proseSpans(text: string): ProseSpan[] {
  const out: ProseSpan[] = [];
  // ⚠ The capture keeps the delimiters out; an odd `**` simply never matches
  // the pair and falls through as ordinary text.
  const parts = text.split(/\*\*([^*]+)\*\*/g);
  for (let i = 0; i < parts.length; i += 1) {
    const chunk = parts[i] ?? '';
    if (!chunk) continue;
    out.push({ text: chunk, bold: i % 2 === 1 });
  }
  return out;
}

const HEADING = /^(#{1,3})\s+(.*)$/;
const BULLET = /^\s*[-*•]\s+(.*)$/;

/**
 * Prose → blocks.
 *
 * ⚠ CONSECUTIVE BULLETS BECOME ONE LIST. Rendering each as its own `<ul>` gives
 * every bullet its own vertical margin, which reads as a stack of paragraphs
 * with dots rather than as a list.
 */
export function proseBlocks(text: string): ProseBlock[] {
  const blocks: ProseBlock[] = [];
  const lines = String(text ?? '').replace(/\r\n/g, '\n').split('\n');
  let para: string[] = [];
  let list: ProseSpan[][] | null = null;

  const flushPara = () => {
    if (!para.length) return;
    blocks.push({ kind: 'para', spans: proseSpans(para.join(' ')) });
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    blocks.push({ kind: 'list', items: list });
    list = null;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushPara(); flushList(); continue; }

    const h = HEADING.exec(line);
    if (h) {
      flushPara(); flushList();
      blocks.push({
        kind: 'heading',
        level: (h[1] as string).length as 1 | 2 | 3,
        spans: proseSpans(h[2] as string),
      });
      continue;
    }

    const b = BULLET.exec(line);
    if (b) {
      flushPara();
      list = list ?? [];
      list.push(proseSpans(b[1] as string));
      continue;
    }

    flushList();
    para.push(line.trim());
  }
  flushPara();
  flushList();
  return blocks;
}
